// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ramps and wire habitrails (M3-06).
//
// Both are read from the **simulation's own `Path`**, not re-sampled from the definition's
// control points. A Catmull-Rom curve depends on how densely it was sampled, so a picture
// built from a second sampling of the same spline is a slightly different curve from the one
// the ball rides — close enough to look fine and far enough that a ball on a long habitrail
// visibly drifts off its own wire towards the middle. One curve, sampled once, used by both.
//
// A ramp's walls sit exactly where its colliders are: the solver builds them with
// `path.offsetPolyline(±width/2)`, a purely horizontal offset, and so does this. The floor
// sits at the path's own height, which is the height the ramp physics puts under the ball.
//
// **A habitrail is drawn as its two wires and nothing else.** A real one hangs off posts
// screwed to the playfield, and those posts would be geometry the solver knows nothing about
// standing in the middle of open playfield that a ball rolls across — the exact lie this
// whole layer exists not to tell. If a machine wants posts under its habitrail it can state
// them as colliders, and then they will be drawn like every other post.

import { Group, Mesh, BufferAttribute, BufferGeometry, type Material } from 'three'
import type { LoadedMachine, RampLayer, WireLayer } from '@core/machine'
import type { Ramp } from '@core/sim/devices/Ramp'
import type { WireGuide } from '@core/sim/devices/WireGuide'
import type { Path } from '@core/sim/Path'
import { BALL_RADIUS } from '@core/sim/constants'
import { uprightCylinder, type PartPalette } from './partsKit'
import { offsetPath, stripTriangles, stripNormals, tubeTriangles, type Vec3 } from './tubePath'
import { addAuthoredRamp } from './authoredRamps'
import { addAuthoredWire } from './authoredWires'

export interface BuiltRamps {
  readonly group: Group
  /** Geometries the caller must dispose. Materials belong to the palette. */
  readonly geometries: readonly BufferGeometry[]
  /** Layers drawn as nothing, each with the reason. */
  readonly skipped: readonly string[]
}

/** Build every ramp and habitrail the machine declares. */
export function buildRamps(machine: LoadedMachine, palette: PartPalette, manufactured = false, steel?:Material): BuiltRamps {
  const group = new Group()
  group.name = 'ramps'
  const geometries: BufferGeometry[] = []
  const skipped: string[] = []

  for (const layer of machine.definition.layers) {
    if (layer.kind === 'ramp') {
      buildRamp(layer, machine, palette, group, geometries, skipped, manufactured, steel)
    } else if (layer.kind === 'wire') {
      buildWire(layer, machine, palette, group, geometries, skipped)
    } else if (layer.kind === 'queue') {
      // The trough is inside the cabinet, below the wood. Nobody has ever seen one during a
      // game, and drawing it would put six balls in shot of the player camera.
      skipped.push(`${layer.id}: a ball trough lives under the playfield`)
    }
  }

  for (const collider of machine.definition.colliders) {
    if (collider.kind !== 'circle' || collider.device === undefined) continue
    const layer = machine.definition.layers.find(l => l.id === collider.device && (l.kind === 'ramp' || l.kind === 'wire'))
    if (layer === undefined) continue
    const runtime = machine.devices.get(layer.id) as Ramp | WireGuide | undefined
    if (runtime === undefined) continue
    const centre = samples(runtime.path)
    const nearest = centre.reduce((a, b) => Math.hypot(b[0]-collider.centre[0], b[1]-collider.centre[1]) < Math.hypot(a[0]-collider.centre[0], a[1]-collider.centre[1]) ? b : a)
    const height = Math.max(.004, nearest[2] - (layer.kind === 'wire' ? .016 : RAMP_THICKNESS))
    const post = uprightCylinder(collider.radius, collider.radius, height, 0, 16)
    post.translate(collider.centre[0], collider.centre[1], 0); geometries.push(post)
    const mesh = new Mesh(post, palette.get('chrome')); mesh.name = collider.id; group.add(mesh)
    if (layer.kind === 'wire') {
      const x = collider.centre[0], y = collider.centre[1]
      addMesh(tubeTriangles([[x-WIRE_LATERAL,y,nearest[2]-WIRE_DROP-WIRE_RADIUS], [x-WIRE_LATERAL,y,height], [x+WIRE_LATERAL,y,height], [x+WIRE_LATERAL,y,nearest[2]-WIRE_DROP-WIRE_RADIUS]], WIRE_RADIUS, 6), palette.get('wire'), collider.id+'.cradle', group, geometries)
    }
  }

  return { group, geometries, skipped }
}

/** A path's samples as points. Read from the simulation, so it is exactly the ball's curve. */
function samples(path: Path): Vec3[] {
  const out: Vec3[] = []
  for (let i = 0; i < path.xs.length; i++) out.push([path.xs[i]!, path.ys[i]!, path.zs[i]!])
  return out
}

function addMesh(
  positions: Float32Array,
  material: Material,
  name: string,
  group: Group,
  geometries: BufferGeometry[],
  normals?:Float32Array,
): void {
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  if(normals)geometry.setAttribute('normal',new BufferAttribute(normals,3));else geometry.computeVertexNormals()
  geometry.computeBoundingSphere()
  geometries.push(geometry)
  const mesh = new Mesh(geometry, material)
  mesh.name = name
  group.add(mesh)
}

/**
 * A ramp: a floor at the path's own height, a wall each side on the collider line, and a
 * steel rail capping each wall.
 *
 * The floor is given an underside. A ramp is a solid object seen from below whenever the
 * camera is anywhere near playfield level, and a single-sided floor disappears from those
 * angles, leaving the ball apparently climbing between two floating walls.
 */
function buildRamp(
  layer: RampLayer,
  machine: LoadedMachine,
  palette: PartPalette,
  group: Group,
  geometries: BufferGeometry[],
  skipped: string[],
  manufactured: boolean,
  steel?:Material,
): void {
  const runtime = machine.devices.get(layer.id) as Ramp | undefined
  if (runtime === undefined) {
    skipped.push(`${layer.id}: no ramp of that id in the loaded machine`)
    return
  }

  const centre = samples(runtime.path)
  const half = runtime.width / 2
  const floorLeft = offsetPath(centre, half, -.00004)
  const floorRight = offsetPath(centre, -half, -.00004)
  const authored = addAuthoredRamp(machine.definition.machineId, layer.id,
    runtime.width, layer.wallHeight, centre, group, palette, geometries, steel)
  const plastic = palette.acrylic(authored ? 'rampClearSheet' : manufactured ? 'clearRamp' : 'rampPlastic')

  addMesh(stripTriangles(floorLeft, floorRight), plastic, `${layer.id}.floor`, group, geometries,stripNormals(floorLeft,floorRight))

  const underLeft = offsetPath(centre, half, -RAMP_THICKNESS-.00004)
  const underRight = offsetPath(centre, -half, -RAMP_THICKNESS-.00004)
  // Right before left: the same strip wound the other way, so it faces down.
  addMesh(stripTriangles(underRight, underLeft), plastic, `${layer.id}.underside`, group, geometries,stripNormals(underRight,underLeft))
  // Close the existing 3mm sheet, inside its current footprint, with a polished cut edge.
  const last=centre.length-1,edges=[
    [underLeft,floorLeft],[floorRight,underRight],
    [[underRight[0]!,underLeft[0]!],[floorRight[0]!,floorLeft[0]!]],
    [[underLeft[last]!,underRight[last]!],[floorLeft[last]!,floorRight[last]!]],
  ] as const
  const edgePositions:number[]=[],edgeNormals:number[]=[]
  for(const [a,b] of edges){edgePositions.push(...stripTriangles(a,b));edgeNormals.push(...stripNormals(a,b))}
  addMesh(new Float32Array(edgePositions),palette.acrylic('rampClearEdge'),`${layer.id}.cutEdges`,group,geometries,new Float32Array(edgeNormals))
  if (manufactured && !authored) for (const entry of [true, false]) {
    const end = centre[entry ? 0 : centre.length-1]!
    const shoe = centre.filter(p => Math.hypot(p[0]-end[0],p[1]-end[1],p[2]-end[2]) < .025)
    if (shoe.length > 1) addMesh(stripTriangles(offsetPath(shoe,half,-.00001),offsetPath(shoe,-half,-.00001)),palette.get('coilArm'),`${layer.id}.${entry ? 'entryShoe' : 'exitShoe'}`,group,geometries)
  }

  const wallHeight = layer.wallHeight
  for (const [side, sign] of [
    ['left', 1],
    ['right', -1],
  ] as const) {
    const foot = offsetPath(centre, half * sign, 0)
    // Straight up, not up the ramp's own normal. A real vacuum-formed wall leans back with
    // the floor it is moulded to, and the collider does not: the solver's wall is a planar
    // polyline that acts at every height, so a wall drawn leaning is up to seven millimetres
    // away from the surface the ball actually hits by the time it reaches the top. Vertical
    // is both the collider and, on a 1-in-5 gradient, indistinguishable by eye.
    const top: Vec3[] = foot.map((p) => [p[0], p[1], p[2] + wallHeight])
    // Two-sided: a ramp wall is seen from outside as part of the machine and from inside by
    // anyone watching a ball go up it, and both views happen in the same second.
    if (!authored) addMesh(
      stripTriangles(foot, top, true),
      plastic,
      `${layer.id}.wall.${side}`,
      group,
      geometries,
      stripNormals(foot,top,true),
    )
    addMesh(
      tubeTriangles(top, RAMP_RAIL_RADIUS, 6),
      palette.get('chrome'),
      `${layer.id}.rail.${side}`,
      group,
      geometries,
    )
  }
}

/**
 * A habitrail: two wires with the ball cradled between them.
 *
 * The wires are placed so they *touch* the ball rather than so they look about right — the
 * simulation carries the ball's centre along the path, so a wire at lateral `L` and drop `D`
 * grazes the ball exactly when `hypot(L, D)` is the ball's radius. Get that wrong and the
 * ball either floats above its own wireform or sinks through it, which is the single most
 * noticeable thing a habitrail can do wrong.
 */
function buildWire(
  layer: WireLayer,
  machine: LoadedMachine,
  palette: PartPalette,
  group: Group,
  geometries: BufferGeometry[],
  skipped: string[],
): void {
  const runtime = machine.devices.get(layer.id) as WireGuide | undefined
  if (runtime === undefined) {
    skipped.push(`${layer.id}: no habitrail of that id in the loaded machine`)
    return
  }

  const floor = samples(runtime.path)
  if(addAuthoredWire(machine.definition.machineId,layer.id,floor,group,palette,geometries))return
  const centre = floor.map(p=>[p[0],p[1],p[2]+BALL_RADIUS] as Vec3)
  const wire = palette.get('wire')
  for (const [side, sign] of [
    ['left', 1],
    ['right', -1],
  ] as const) {
    const rail = offsetPath(centre, WIRE_LATERAL * sign, -WIRE_DROP)
    addMesh(
      tubeTriangles(rail, WIRE_RADIUS, 6),
      wire,
      `${layer.id}.${side}`,
      group,
      geometries,
    )
  }
}

/** Ramp floor thickness, metres. Thin, because a real ramp is a sheet of vacuum-formed PETG. */
const RAMP_THICKNESS = 0.003
const RAMP_RAIL_RADIUS = 0.0018

/**
 * Where a habitrail's two wires sit relative to the ball's centre.
 *
 * `hypot(WIRE_LATERAL, WIRE_DROP)` is the ball's radius, so each wire is tangent to the ball
 * it carries. Chosen as an angle rather than as two independent numbers: 40° below the
 * horizontal is a cradle deep enough to look like it holds the ball and shallow enough to
 * leave most of it visible.
 */
const WIRE_LATERAL = (BALL_RADIUS + .0013 + .00001) * Math.cos(0.698)
const WIRE_DROP = (BALL_RADIUS + .0013 + .00001) * Math.sin(0.698)
const WIRE_RADIUS = 0.0013
